import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@zippeditoolsjs/ui-web';

import { support_phone_number, whatsAppUrl } from 'core/slices/constants';
import LoginForm from '../../redux/containers/login/LoginForm';
import PhoneNumberForm from '../../redux/containers/login/PhoneNumberForm';
import OPTCode from '../../redux/containers/login/OPTCode';
import TOTPCode from './mfa/TOTPCode';
import MfaMethodChooser from './mfa/MfaMethodChooser';
import MfaEnrollmentChooser from './mfa/MfaEnrollmentChooser';
import TotpEnrollment, { STEPS as TOTP_STEPS } from './mfa/TotpEnrollment';
import { stageFor } from './policy/enrollmentStages';
import { TotpSuccess } from '../ui/totp';
import useLoginFlow, { LOGIN_VIEWS } from './flow/useLoginFlow';
import LoginLayout from './layout/LoginLayout';

// Composition-only screen: all sign-in, MFA and session logic lives in
// useLoginFlow; this component just picks which view to render.
export default function Welcome(props) {
  const { t } = useTranslation();
  const [enrollStep, setEnrollStep] = useState(TOTP_STEPS.QR);

  const {
    currentView,
    loginErrorKey,
    setLoginErrorKey,
    enrollmentDismissible,
    releasingSession,
    isLogin,
    mfaResolved,
    language,
    tenantID,
    email,
    password,
    PhoneNumber,
    phoneRef,
    captchaCode,
    resolverAuth,
    sendingOPT,
    finishedSMS,
    countdown,
    showButton,
    isLoading,
    isLoadingRedirect,
    handleChangeEmail,
    handleChangePassword,
    SignInWithEmail,
    verifySMSCode,
    verifyTotpCode,
    beginSmsEnrollment,
    setUpRecaptha,
    logInValidation,
    triggerResend,
    sendFirstSMS,
    resetChallengeRecaptcha,
    setPhoneNumber,
    setCaptchaCode,
    setShowButton,
    setCountdown,
    setIsLoading,
    setIsLoadingRedirect,
    setResolverAuth,
    showMfaEnrollmentChooser,
    switchToSmsChallenge,
    openMethodChooser,
    backToPhoneEnrollment,
    backToTotpChallenge,
    finishTotpEnrollment,
    finishSmsEnrollment,
    backFromEnrollment,
    enrollmentOrigin,
    pickTotpEnrollment,
    pickSmsEnrollment,
    skipMfaEnrollment,
    dontAskMfaEnrollmentAgain,
    dontAskMonths,
    secondFactorOptInFor,
    onTotpEnrolled,
    sessionFactors,
  } = useLoginFlow({
    getCSRFToken: props.getCSRFToken,
    getSessionCookie: props.getSessionCookie,
    getWelcomeMessage: props.getWelcomeMessage,
    isLoadingSession: props.isLoadingSession,
    hasSessionCookie: props.hasSessionCookie,
    hasSessionError: props.hasSessionError,
  });

  // Clearing the key on the way out is what lets the same failure raise a second
  // toast; the flow drops stale ones with toast.dismissAll.
  useEffect(() => {
    if (!loginErrorKey) return;
    toast.error(t(loginErrorKey));
    setLoginErrorKey(null);
  }, [loginErrorKey, setLoginErrorKey, t]);

  const fullWhatsAppUrl = `${whatsAppUrl}${support_phone_number}?text=${t(
    'heimdal_app.Login.help_initial_text',
  )}`;

  const stage = stageFor(currentView, enrollStep, isLogin);

  return (
    <LoginLayout
      stage={stage}
      welcomeMessage={props.welcomeMessage}
      helpUrl={fullWhatsAppUrl}>
      {currentView === LOGIN_VIEWS.SMS_SUCCESS ? (
        <TotpSuccess
          autoEnter
          seconds={5}
          onEnter={finishSmsEnrollment}
          enrolledFactors={sessionFactors}
        />
      ) : currentView === LOGIN_VIEWS.TOTP_ENROLLMENT ? (
        <TotpEnrollment
          tenantID={tenantID}
          onDone={finishTotpEnrollment}
          onEnrolled={onTotpEnrolled}
          onBack={backFromEnrollment}
          onStepChange={setEnrollStep}
          setLoginErrorKey={setLoginErrorKey}
          busy={releasingSession}
          {...secondFactorOptInFor('totp')}
          showBackToChooser={enrollmentOrigin === 'chooser'}
        />
      ) : currentView === LOGIN_VIEWS.MFA_ENROLLMENT_CHOOSER ? (
        <MfaEnrollmentChooser
          onPickTotp={pickTotpEnrollment}
          onPickSms={pickSmsEnrollment}
          onLater={enrollmentDismissible ? skipMfaEnrollment : undefined}
          onDontAskAgain={dontAskMfaEnrollmentAgain}
          canDismiss={enrollmentDismissible}
          dontAskMonths={dontAskMonths}
          busy={releasingSession}
        />
      ) : currentView === LOGIN_VIEWS.MFA_METHOD_CHOOSER ? (
        <MfaMethodChooser
          resolver={resolverAuth}
          onPickTotp={backToTotpChallenge}
          onPickSms={switchToSmsChallenge}
          onBack={backToTotpChallenge}
        />
      ) : currentView === LOGIN_VIEWS.TOTP_CODE ? (
        <TOTPCode
          code={captchaCode}
          resolver={resolverAuth}
          sendingOPT={sendingOPT}
          setCaptchaCode={setCaptchaCode}
          onTryAnotherWay={mfaResolved ? undefined : openMethodChooser}
          setLoginErrorKey={setLoginErrorKey}
          verifyTotpCode={verifyTotpCode}
          {...secondFactorOptInFor('totp')}
        />
      ) : currentView === LOGIN_VIEWS.PHONE_ENROLLMENT ? (
        <PhoneNumberForm
          PhoneNumber={PhoneNumber}
          setPhoneNumber={setPhoneNumber}
          setUpRecaptha={setUpRecaptha}
          sendingSMS={sendingOPT}
          busy={releasingSession}
          onSendSMS={beginSmsEnrollment}
          onBack={enrollmentOrigin === 'chooser' ? backFromEnrollment : undefined}
          onLater={
            enrollmentOrigin === 'chooser' ? undefined : backFromEnrollment
          }
          phoneRef={phoneRef}
        />
      ) : currentView === LOGIN_VIEWS.SMS_CODE ? (
        <OPTCode
          code={captchaCode}
          resolver={resolverAuth}
          isLogin={isLogin}
          showCode={currentView === LOGIN_VIEWS.SMS_CODE}
          PhoneNumber={PhoneNumber}
          setCaptchaCode={setCaptchaCode}
          verifySMSCode={verifySMSCode}
          sendingOPT={sendingOPT}
          busy={releasingSession}
          logInValidation={logInValidation}
          triggerResend={triggerResend}
          finishedSMS={finishedSMS}
          sendFirstSMS={sendFirstSMS}
          countdown={countdown}
          showButton={showButton}
          setShowButton={setShowButton}
          setCountdown={setCountdown}
          setIsLoading={setIsLoading}
          setUpRecaptha={setUpRecaptha}
          onUseAnotherMethod={mfaResolved ? undefined : openMethodChooser}
          resetChallengeRecaptcha={resetChallengeRecaptcha}
          onBackToPhone={backToPhoneEnrollment}
          {...secondFactorOptInFor('sms')}
        />
      ) : (
        <LoginForm
          language={language}
          tenantID={tenantID}
          setLoginErrorKey={setLoginErrorKey}
          email={email}
          password={password}
          handleChangeEmail={handleChangeEmail}
          handleChangePassword={handleChangePassword}
          SignInWithEmail={SignInWithEmail}
          resolveTenant={props.resolveTenant}
          getSessionCookie={props.getSessionCookie}
          setIsLoading={setIsLoading}
          showMfaEnrollmentChooser={showMfaEnrollmentChooser}
          setResolverAuth={setResolverAuth}
          setIsLoadingRedirect={setIsLoadingRedirect}
          isLoading={isLoading}
          isLoadingRedirect={isLoadingRedirect}
        />
      )}
    </LoginLayout>
  );
}
